<template lang="pug">
web-app-layout(title="Create a Giving Wallet")
  .create-wallet-page
    .page-container
      .wallet-card
        img(src="/images/ic_money.png" alt="Wallet").wallet-icon

      .intro-text(v-html="introText")

      .features
        ExpansionPanel(
          v-for="feature in features"
          :key="feature.title"
          :title="feature.title"
          :description="feature.description"
          :initially-expanded="true"
        )

      .terms-section
        label.checkbox-label
          input(
            type="checkbox"
            v-model="isTermsRead"
          )
          span.checkbox-text
            | By opening a wallet, you agree to the terms of the donor-advised fund, Spotlight's
            |
            a(
              href="https://www.spotlight.xyz/legal-stuff/terms-and-conditions"
              target="_blank"
              rel="noopener"
            ) Terms & Conditions
            |
            | and
            |
            a(
              href="https://www.spotlight.xyz/legal-stuff/privacy-policy"
              target="_blank"
              rel="noopener"
            ) Privacy Policy
            | .

        .legal-text(v-html="legalText")

      el-button(
        type="primary"
        :disabled="!isTermsRead || isLoading"
        :loading="isLoading"
        @click="openWallet"
      ).open-wallet-btn Open Wallet

      .error-message(v-if="error") {{ error }}
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  onMounted,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import ExpansionPanel from './ExpansionPanel.vue';
import { makeRequest } from '@/graphs';
import {
  initiateWallet,
  initiateWalletForOrg,
  benevityDetails,
} from '@/graphs/gql';
import globalShared from '@/shared';

export default defineComponent({
  name: 'CreateGivingWallet',
  components: {
    WebAppLayout,
    ExpansionPanel,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { getAuthToken } = globalShared();

    // State
    const isTermsRead = ref(false);
    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const orgId = ref<string | null>(null);

    // Extract orgId from query params
    if (route.query.orgId) {
      orgId.value = route.query.orgId as string;
      console.log('[CREATE WALLET] ========== WALLET TYPE: ORGANIZATION ==========');
      console.log('[CREATE WALLET] Organization ID:', orgId.value);
      console.log('[CREATE WALLET] This will create a wallet for the ORGANIZATION, not the USER');
      console.log('[CREATE WALLET] ===================================================');
    } else {
      console.log('[CREATE WALLET] ========== WALLET TYPE: USER (PERSONAL) ==========');
      console.log('[CREATE WALLET] This will create a PERSONAL wallet for the logged-in user');
      console.log('[CREATE WALLET] ========================================================');
    }

    // Content
    const introText = '<p>Spotlight makes investing in social good a snap. Keep in mind, '
      + 'funds invested in the fund are earmarked immediately for charitable giving. '
      + 'For this reason, <strong>you cannot withdraw any money from your Giving Wallet</strong>. '
      + 'Once in the wallet, contributions are irrevocable and must eventually make their way '
      + 'to an IRS-qualified public charity. Your Giving Wallet has the perfect qualities '
      + 'of a good mate.</p>';

    const legalText = '<p>Your Spotlight Giving Wallet is established with the '
      + '<strong>American Online Giving Fund (AOGF)</strong>, a U.S. 501(c)(3) donor-advised '
      + 'fund (EIN 81-0739440). When you add funds to your wallet, your contribution is treated '
      + 'as a donation to AOGF. You will be able to provide donor advice through the Spotlight '
      + 'app to recommend grants to eligible charities listed in the Benevity directory.</p>'
      + '<p>AOGF disburses donations to recipient charities <strong>once per month</strong>. '
      + 'Recipient charities receive your donation <strong>minus Benevity\'s standard '
      + 'transaction fees</strong>. All funds added to your wallet are <strong>non-refundable'
      + '</strong> and may only be used for charitable purposes.</p>'
      + '<p>Funds in your wallet cannot be used for political contributions, campaign financing, '
      + 'PAC donations, event tickets, membership dues, goods or services, or any transaction '
      + 'that provides a personal benefit to you or any other individual.</p>';

    const features = [
      {
        title: 'SMART',
        description: 'Impact doesn\'t always happen in silos. Sometimes you want to give to one '
          + 'charity, and other times you might want to group charities into your idea of a '
          + 'coalition fund. Within the wallet, and with one donation, you can make flexible '
          + 'decisions with one click to one or multiple organizations.',
      },
      {
        title: 'EFFICIENT',
        description: 'The words simple and taxes used together might seem like an oxymoron, but '
          + 'the Giving Wallet makes it easier to give to the charities and causes close to your '
          + 'heart. As soon as you make a donation, you are eligible for an immediate tax '
          + 'deduction. You can budget how much of the discretionary funds you want to earmark '
          + 'for giving and then be ready to act when needed.',
      },
      {
        title: 'ORGANIZED',
        description: 'You don\'t have to keep track of every gift acknowledgment from every '
          + 'charity you support—just the receipts from your Giving Wallet donor-advised fund '
          + 'contributions. Our one-stop setup will save you time from having to search for tax '
          + 'receipts in emails.',
      },
      {
        title: 'TRUSTWORTHY',
        description: 'The charities listed in your Giving Wallet directory are vetted across '
          + '1200 lists to ensure that you give to trustworthy organizations. You do not have to '
          + 'worry about fraudulent container organizations or organizations that may have ties '
          + 'to terrorism. The public charity sponsoring the account conducts all the due '
          + 'diligence to ensure that the funds will be used for charitable purposes. Please note '
          + 'that we do not support campaign financing or PAC donations per our Terms of Use.',
      },
      {
        title: 'FRIENDLY',
        description: 'Your wallet also lets you send funds wallet-to-wallet making it easy to '
          + 'send money to your philanthropic friends and support their charitable interests. '
          + 'Those charitable birthday or wedding day gifts can now be transferred in-app to a '
          + 'friend\'s wallet. Employers can do the same and make charitable gifts directly to '
          + 'your wallet. Tax-wise, you won\'t be on the hook for money received for fundraising '
          + 'campaigns because it is not income, and you can accept funds to donate to the '
          + 'charities you have in mind freely.',
      },
    ];

    // Methods
    const openWallet = async () => {
      if (!isTermsRead.value) {
        return;
      }

      isLoading.value = true;
      error.value = null;

      try {
        const token = getAuthToken();

        let response;
        
        if (orgId.value) {
          // Create wallet for ORGANIZATION
          console.log('[CREATE ORG WALLET] Creating wallet for organization:', orgId.value);
          response = await makeRequest(
            'mutation',
            initiateWalletForOrg,
            { orgId: orgId.value },
            { token },
          );
        } else {
          // Create wallet for USER
          console.log('[CREATE USER WALLET] Creating wallet for user');
          response = await makeRequest(
            'mutation',
            initiateWallet,
            {},
            { token },
          );
        }

        if (response?.errors) {
          console.error('[CREATE WALLET ERROR]', response.errors);
          throw new Error(response.errors[0]?.message || 'Failed to initiate wallet');
        }

        console.log('[CREATE WALLET SUCCESS] Wallet created for:', orgId.value || 'user');
        ElMessage.success('Wallet initiated successfully');

        // Pass orgId to mailing address page
        const mailingAddressQuery: any = {};
        if (orgId.value) {
          mailingAddressQuery.orgId = orgId.value;
        }
        
        router.push({
          path: '/web/wallet/mailing-address',
          query: mailingAddressQuery,
        });
      } catch (err: any) {
        console.error('[CREATE WALLET ERROR]', err.message);
        error.value = err.message || 'Wallet initiation failed. Please try again.';
        if (error.value) {
          ElMessage.error(error.value);
        }
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      // Component mounted
    });

    return {
      isTermsRead,
      isLoading,
      error,
      introText,
      legalText,
      features,
      openWallet,
    };
  },
});
</script>

<style lang="scss" scoped>
.create-wallet-page {
  padding: 26px 20px 100px;
  background: #fff;
  min-height: 100vh;
}

.page-container {
  max-width: 800px;
  margin: 0 auto;
}

.wallet-card {
  height: 22vh;
  width: 100%;
  max-width: 350px;
  background: linear-gradient(135deg, #005C6B 0%, #5CC4BC 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 26px;
  margin-top: 48px;
  padding: 20px;
}

.wallet-icon {
  max-width: 50%;
  max-height: 50%;
  object-fit: contain;
}

.intro-text {
  margin-bottom: 26px;
  font-size: 16px;
  line-height: 1.6;
  color: #333;

  :deep(p) {
    margin: 0;
  }

  :deep(strong) {
    font-weight: 600;
  }
}

.features {
  margin-bottom: 16px;
}

.terms-section {
  margin-bottom: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  cursor: pointer;

  input[type="checkbox"] {
    cursor: pointer;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
}

.checkbox-text {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  font-weight: 700;

  a {
    color: #5CC4BC;
    text-decoration: underline;
    font-weight: 700;

    &:hover {
      color: #005C6B;
    }
  }
}

.legal-text {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  padding-left: 0;
  font-style: italic;

  :deep(p) {
    margin: 0 0 12px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(strong) {
    font-weight: 600;
    color: #333;
  }
}

.open-wallet-btn {
  width: 100%;
  padding: 16px 34px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  height: auto;
  margin-top: 32px;
  background-color: #1EBEAF !important;
  border-color: #1EBEAF !important;

  &:hover {
    background-color: #1aaa9d !important;
    border-color: #1aaa9d !important;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.error-message {
  margin-top: 16px;
  padding: 12px;
  background: #fee;
  color: #c33;
  border-radius: 8px;
  font-size: 14px;
}

@media (max-width: 768px) {
  .create-wallet-page {
    padding: 20px 16px 80px;
  }
}
</style>
